import type {
    FishGroupTimelineDto,
    TimelineEventDto,
    TimelineFishGroupDto,
    TimelineOperationDto,
    TimelineStayDto,
    TimelineTransferDto,
    TimelineWeightDto,
} from '@/services/types';
import { TimelineWeightStatus as WeightStatusDto } from '@/services/types';
import { parseISOString } from '@/utils/date';

import { mapStockingCycleToDomain } from '../stockingCycles/mappers';

import type {
    SiteTimeline,
    TimelineEvent,
    TimelineFishGroup,
    TimelineOperation,
    TimelineStay,
    TimelineTransfer,
    TimelineWeight,
} from './siteTimeline.types';

const LIVE_STATUSES = new Set<TimelineFishGroup['status']>(['pending', 'active']);

function toDate(value: string | null | undefined): Date | null {
    return parseISOString(value ?? null);
}

/** Required timestamps are in the contract; an unparsable one falls back to the epoch. */
function toRequiredDate(value: string): Date {
    return toDate(value) ?? new Date(0);
}

function mapOperation(dto: TimelineOperationDto): TimelineOperation {
    return { id: dto.id, type: dto.operationType, reason: dto.reason ?? null };
}

const NO_WEIGHT: TimelineWeight = { status: 'noData', livingWeight: null, day: null };

/** A weight is shown only with its number and day; anything less reads as no data. */
export function mapTimelineWeight(dto: TimelineWeightDto | null | undefined): TimelineWeight {
    if (!dto) return NO_WEIGHT;
    if (dto.status === WeightStatusDto.notLicensed) {
        return { status: 'notLicensed', livingWeight: null, day: null };
    }
    const day = toDate(dto.day);
    if (dto.status === WeightStatusDto.available && typeof dto.livingWeight === 'number' && day) {
        return { status: 'available', livingWeight: dto.livingWeight, day };
    }
    return NO_WEIGHT;
}

function readFlag(data: TimelineEventDto['eventData'], key: string): boolean {
    if (!data || typeof data !== 'object') return false;
    return data[key] === true;
}

function mapEvent(dto: TimelineEventDto): TimelineEvent {
    return {
        id: dto.id,
        type: dto.eventType,
        date: toRequiredDate(dto.eventDate),
        cageId: dto.cageId ?? null,
        movedOffSite: readFlag(dto.eventData, 'movedOffSite'),
        movedFromOffSite: readFlag(dto.eventData, 'movedFromOffSite'),
        operation: dto.operation ? mapOperation(dto.operation) : null,
        fishCountBefore: dto.fishCountBefore ?? null,
        fishCountAfter: dto.fishCountAfter ?? null,
        notes: dto.notes ?? null,
    };
}

function mapStay(dto: TimelineStayDto): TimelineStay {
    return {
        id: dto.id,
        cageId: dto.cageId,
        cageName: dto.cageName,
        from: toRequiredDate(dto.from),
        to: toDate(dto.to),
        reason: dto.reason ?? null,
        bioscopes: (dto.bioscopes ?? []).map((coverage) => ({
            bioscopeId: coverage.bioscopeId,
            from: toRequiredDate(coverage.from),
            to: toDate(coverage.to),
        })),
        firstMeasurementDate: toDate(dto.firstMeasurementDate),
        weight: mapTimelineWeight(dto.weight),
    };
}

export function mapTimelineGroup(dto: TimelineFishGroupDto): TimelineFishGroup {
    return {
        id: dto.id,
        name: dto.name,
        status: dto.status,
        isLive: LIVE_STATUSES.has(dto.status),
        stockingCycleId: dto.stockingCycleId ?? null,
        fishType: {
            id: dto.fishType?.id ?? 0,
            name: dto.fishType?.name ?? '',
            shortName: dto.fishType?.shortName ?? '',
        },
        lifecycleStartedAt: toDate(dto.lifecycleStartedAt),
        lifecycleEndedAt: toDate(dto.lifecycleEndedAt),
        endedBy: dto.endedBy ? mapOperation(dto.endedBy) : null,
        parentIds: dto.parentIds ?? [],
        childIds: dto.childIds ?? [],
        offSiteLinks: (dto.offSiteLinks ?? []).map((link) => link.direction),
        stays: (dto.stays ?? []).map(mapStay),
        events: (dto.events ?? []).map(mapEvent),
        firstMeasurementDate: toDate(dto.firstMeasurementDate),
        estimatedCount: dto.estimatedCount ?? null,
        weight: mapTimelineWeight(dto.weight),
    };
}

function mapTransfer(dto: TimelineTransferDto): TimelineTransfer {
    return {
        id: dto.id,
        sourceFishGroupId: dto.sourceFishGroupId,
        destinationFishGroupId: dto.destinationFishGroupId,
        date: toRequiredDate(dto.transferDate),
        operation: mapOperation(dto.operation),
        estimatedFishCount: dto.estimatedFishCount ?? null,
    };
}

export function mapSiteTimelineToDomain(dto: FishGroupTimelineDto): SiteTimeline {
    return {
        locationId: dto.locationId,
        cycle: dto.cycle ? mapStockingCycleToDomain(dto.cycle) : null,
        groups: (dto.groups ?? []).map(mapTimelineGroup),
        transfers: (dto.transfers ?? []).map(mapTransfer),
        externalGroups: (dto.externalGroups ?? []).map((group) => ({
            id: group.id,
            name: group.name,
            status: group.status,
        })),
        generatedAt: toRequiredDate(dto.generatedAt),
    };
}
